import React, { useState, useEffect } from 'react';
import styles from 'css_modules/Toast.module.css';
import { ReactComponent as InfoIcon } from 'assets/svg/2_info.svg';
import { ReactComponent as CancelIcon } from 'assets/svg/2_cancel.svg';

export const ToastContext = React.createContext(null);

export const Toast = ({ children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [text, setText] = useState('');
    const [style, setStyle] = useState({});
    const [timeoutId, setTimeoutId] = useState(null);

    const STORAGE_KEY = 'toast_data';

    const readLayoutFromSession = () => {
        const x = sessionStorage.getItem('toast_x');
        const y = sessionStorage.getItem('toast_y');
        const width = sessionStorage.getItem('toast_width');

        return {
            left: x !== null ? `${parseInt(x, 10)}px` : '0',
            top: y !== null ? `${parseInt(y, 10)}px` : '0',
            width: width ?? '100%',
        };
    };

    const clearStoredToast = () => {
        sessionStorage.removeItem(STORAGE_KEY);
    };

    const hideToast = () => {
        setIsVisible(false);
        setTimeout(() => setIsMounted(false), 400);

        clearStoredToast();

        if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }
    };

    const showToast = (message) => {
        const now = Date.now();

        const toastData = {
            message,
            createdAt: now,
            duration: 5000,
        };

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toastData));

        setText(message);
        setStyle(readLayoutFromSession());
        setIsMounted(true);

        requestAnimationFrame(() => setIsVisible(true));

        const id = setTimeout(() => {
            hideToast();
        }, toastData.duration);

        setTimeoutId(id);
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return;

        try {
            const { message, createdAt, duration } = JSON.parse(stored);
            const elapsed = Date.now() - createdAt;

            if (elapsed >= duration) {
                clearStoredToast();
                return;
            }

            setText(message);
            setStyle(readLayoutFromSession());
            setIsMounted(true);

            requestAnimationFrame(() => setIsVisible(true));

            const remaining = duration - elapsed;

            const id = setTimeout(() => {
                hideToast();
            }, remaining);

            setTimeoutId(id);
        } catch (e) {
            clearStoredToast();
        }
    }, []);
    /* eslint-disable react-hooks/exhaustive-deps */
    
    return (
        <ToastContext.Provider value={showToast}>
            {isMounted && (
                <div
                    className={`${styles.toast} ${isVisible ? styles.visible : ''}`}
                    style={style}
                >
                    <div>
                        <InfoIcon />
                        <span className={styles.text}>{text}</span>
                    </div>

                    <button className={styles.closeButton} onClick={hideToast}>
                        <div className={styles.progressWrapper}>
                            <svg className={styles.progressRing} width="40" height="40">
                                <circle
                                    className={styles.progressBg}
                                    cx="20"
                                    cy="20"
                                    r="17"
                                />
                                <circle
                                    className={styles.progressCircle}
                                    cx="20"
                                    cy="20"
                                    r="17"
                                />
                            </svg>
                            <CancelIcon className={styles.cancelIcon} />
                        </div>
                    </button>
                </div>
            )}
            {children}
        </ToastContext.Provider>
    );
};